import { type ClientContext, ContextClient } from '~/services/contextClient';
import MessageStandardsClientFactory from './messageStandardsClient.factory';
import type {
    MessageStandardsClientInterface,
    MessageStandardType,
    TargetMappingType,
    UpdateTargetsRequest,
    UpdateTargetsResponse,
} from './messageStandardsClient.types';
import type { QueryParams, CursorPaginationResponse } from '~/services/api';
import type { AxiosPromise } from 'axios';

class MessageStandardsClient extends ContextClient<MessageStandardsClientInterface> {
    constructor() {
        super(MessageStandardsClientFactory);
    }

    getMessageStandards(
        context: ClientContext,
        params?: QueryParams
    ): AxiosPromise<CursorPaginationResponse<MessageStandardType>> {
        const client = this.getClient(context);
        return client.getMessageStandards(params);
    }

    getMessageStandard(context: ClientContext, messageStandardId: number): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.getMessageStandard(messageStandardId);
    }

    createMessageStandard(context: ClientContext, data: MessageStandardType): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.createMessageStandard(data);
    }

    updateMessageStandard(
        context: ClientContext,
        messageStandardId: number,
        data: MessageStandardType
    ): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.updateMessageStandard(messageStandardId, data);
    }

    deleteMessageStandard(context: ClientContext, messageStandardId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.deleteMessageStandard(messageStandardId);
    }

    getDefaultMessageStandard(context: ClientContext): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.getDefaultMessageStandard();
    }

    updateDefaultMessageStandard(context: ClientContext, data: MessageStandardType): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.updateDefaultMessageStandard(data);
    }

    getMessageStandardsTargetCounts(
        context: ClientContext,
        params: { custom_message_standard_ids: number[] }
    ): AxiosPromise<{ counts: Record<number, number> }> {
        const client = this.getClient(context);
        if (client.getMessageStandardsTargetCounts) {
            return client.getMessageStandardsTargetCounts(params);
        }
        throw new Error(`Get brand standards group counts is not supported by ${client.constructor.name}`);
    }

    getTargetMappings(context: ClientContext): AxiosPromise<{ data: TargetMappingType[] }> {
        const client = this.getClient(context);
        return client.getTargetMappings();
    }

    updateTargets(
        context: ClientContext,
        messageStandardId: number,
        data: UpdateTargetsRequest
    ): AxiosPromise<UpdateTargetsResponse> {
        const client = this.getClient(context);
        return client.updateTargets(messageStandardId, data);
    }
}

export default new MessageStandardsClient();
